import React, { useMemo, useRef, useState } from 'react';
import type { UnitRow } from '../data/joinCore';
import type { Parcel } from '../data/useParcels';
import { LensDef, LENSES, catColor } from '../data/lenses';
import { T } from '../theme/tokens';
import { PanelGrid } from './PanelGrid';
import { columnsForLens } from './lensColumns';

// Every grouping option (except None) is a LENS — its `cat(parcel)` is the group key and its palette
// tints the group dot, so the grid bands exactly the way the map colours. A FIXED set is always
// offered; the active Color-by lens and any active Split-by lenses slot in too (deduped by id).
interface GroupOption { id: string; label: string; lens: LensDef | null; }
const FIXED_GROUPS: GroupOption[] = [
  { id: 'buildStatus', label: 'Build stage', lens: LENSES.buildStatus },
  { id: 'construction', label: 'Master build stage', lens: LENSES.construction },
  { id: 'ghlContract', label: 'Contract status', lens: LENSES.ghlContract },
  { id: 'permitStatus', label: 'Permit status', lens: LENSES.permitStatus },
  { id: 'pctCO', label: '% Complete', lens: LENSES.pctCO },
  { id: 'model', label: 'Model', lens: LENSES.model },
  { id: 'constructionUpgrades', label: 'Construction upgrades', lens: LENSES.constructionUpgrades },
];

export interface PanelUnitsGridProps {
  units: UnitRow[];
  lensDef: LensDef;
  /** Active Split-by lenses → one extra column each (in order). Empty when no split. */
  splitDefs?: LensDef[];
  parcelById: Map<string, Parcel>;
  selectedLotId: string | null;
  onSelectLot: (lotId: string) => void;
  onHoverLot: (lotId: string | null) => void;
  /** Drag-reorder column order, owned by SiteMapApp (so it persists + rides in saved views). */
  colOrder: string[];
  setColOrder: (k: string[]) => void;
}

/** Lens-aware, filter-reactive units grid for the right panel's lower zone. Columns follow the
 *  active Color-by lens (its value per unit) plus one per active Split-by lens — generated by
 *  columnsForLens so the grid always mirrors the map. Sortable headers; click a row to fly the
 *  map + open the Detail panel; hover badges the lot. `units` is already filter-scoped. */
function PanelUnitsGrid({ units, lensDef, splitDefs, parcelById, selectedLotId, onSelectLot, onHoverLot, colOrder, setColOrder }: PanelUnitsGridProps) {
  const lotLabel = (u: UnitRow): string => (u.lotName || u.unitName || u.blockLot || '—').replace(/\s*\(model home\)\s*$/i, '');
  const parcelOf = (u: UnitRow): Parcel | undefined =>
    (u.lotId ? parcelById.get(u.lotId) : undefined) || u.lotIds.map(id => parcelById.get(id)).find(Boolean) || undefined;

  const splits = splitDefs || [];
  const cols = useMemo(() => columnsForLens(lensDef, splits, units, parcelOf),
    [lensDef, splits, units]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drag-to-reorder columns — order is owned by SiteMapApp (persisted + saved into views); a list of
  // non-frozen column keys. PanelGrid keeps Lot frozen-first and appends any new lens columns.
  const onReorder = (ks: string[]) => setColOrder(ks);
  const resetColOrder = () => setColOrder([]);

  // Group options = None + the fixed set + the active Color-by lens + each active Split-by lens
  // (deduped by lens id, so a Color-by/Split that's already in the fixed set isn't repeated).
  const groupOptions = useMemo<GroupOption[]>(() => {
    const out: GroupOption[] = [{ id: 'none', label: 'No grouping', lens: null }, ...FIXED_GROUPS];
    const seen = new Set(out.map(o => o.id));
    if (lensDef && !seen.has(lensDef.id)) { out.push({ id: lensDef.id, label: `Color by: ${lensDef.label}`, lens: lensDef }); seen.add(lensDef.id); }
    for (const s of splits) if (s && !seen.has(s.id)) { out.push({ id: s.id, label: `Split: ${s.label}`, lens: s }); seen.add(s.id); }
    return out;
  }, [lensDef, splits]);

  const [groupBy, setGroupBy] = useState<string>('none');
  // Selected option — falls back to None if the prior selection (e.g. a Color-by/Split lens) is gone.
  const groupOpt = groupOptions.find(o => o.id === groupBy) || groupOptions[0];
  const groupLens = groupOpt.lens;
  // Multi-value lenses (construction upgrades, incentives…) return ONE key per value so a unit bands
  // under each option it has — "Patio - 10x20" lists every unit with a patio. Split on the lens's
  // own separator (newline — upgrade names contain commas, so a comma split would shred them).
  const groupKeyOf = (u: UnitRow): string | string[] => {
    if (!groupLens) return '';
    const p = parcelOf(u);
    const raw = (p ? groupLens.cat(p) : '') || '';
    if (groupLens.multi) {
      const parts = raw.split(groupLens.sep || '\n').map(s => s.trim()).filter(Boolean);
      return parts.length ? parts : ['—'];
    }
    return raw || '—';
  };
  // Export — the grid mirrors its current sort order into this ref so the workbook matches the screen.
  const orderedRef = useRef<UnitRow[]>(units);
  const [xlsxBusy, setXlsxBusy] = useState<string | false>(false);
  const exportXlsx = async () => {
    if (xlsxBusy) return;
    const rows = orderedRef.current && orderedRef.current.length ? orderedRef.current : units;
    if (!rows.length) { setXlsxBusy('0 rows'); setTimeout(() => setXlsxBusy(false), 2200); return; }
    setXlsxBusy('…');
    try {
      const { buildUnitsGridWorkbookBytes } = await import('../export/unitsGridWorkbook');
      const exportCols = cols.map(c => ({
        header: c.header, align: c.align, kind: c.exportKind,
        value: c.exportVal || ((r: UnitRow) => { const s = c.sortVal?.(r); return typeof s === 'number' ? s : (s ?? ''); }),
      }));
      const groupLabel = groupOpt.id === 'none' ? '' : groupOpt.label.replace(/^(Color by|Split):\s*/, '');
      const bytes = await buildUnitsGridWorkbookBytes({
        columns: exportCols, rows,
        meta: { lens: lensDef.label, date: new Date().toLocaleDateString('en-US'), scope: '' },
        group: groupLabel,
      });
      const blob = new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `LFE-Units-in-View-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      setXlsxBusy('✓'); setTimeout(() => setXlsxBusy(p => p === '✓' ? false : p), 1800);
    } catch (e) {
      console.error('units grid xlsx export failed', e);
      setXlsxBusy('Export failed'); setTimeout(() => setXlsxBusy(false), 3000);
    }
  };

  const groupHeader = (key: string, count: number) => {
    const blank = !key || key === '—';
    const dot = groupLens && !blank ? catColor(groupLens, key) : T.sand2;
    return <>
      <span aria-hidden style={{ width: 9, height: 9, borderRadius: 3, flex: '0 0 auto', background: dot, border: '1px solid rgba(0,0,0,.12)' }} />
      <span>{blank ? '—' : key}</span>
      <span className="num" style={{ color: T.inkSoft, fontWeight: 600 }}>{count}</span>
    </>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 160 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 5px 12px', background: T.sand2, borderTop: `1px solid ${T.line}` }}>
        <span className="eyebrow" style={{ margin: 0, color: T.inkSoft, letterSpacing: '.07em', fontWeight: 700 }}>
          UNITS IN VIEW · {units.length.toLocaleString('en-US')}
        </span>
        {colOrder.length > 0 && (
          <button onClick={resetColOrder} title="Reset columns to their default order"
            style={{ fontSize: 10.5, fontWeight: 600, color: T.inkSoft, background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 2px', textDecoration: 'underline' }}>
            Reset columns
          </button>
        )}
        <button onClick={exportXlsx} disabled={!!xlsxBusy} title="Export the units in view (current columns + sort order) to Excel"
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
            color: T.maroon, background: T.paper, border: `1px solid ${T.line}`, borderRadius: 6, padding: '3px 9px',
            cursor: xlsxBusy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
          <span aria-hidden>{typeof xlsxBusy === 'string' ? xlsxBusy : '↓'}</span> Excel
        </button>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span className="eyebrow" style={{ margin: 0, color: T.inkSoft, fontWeight: 600 }}>Group</span>
          <select value={groupOpt.id} onChange={e => setGroupBy(e.target.value)} title="Group the units list by a value (the active Color-by + any Split-by also appear)"
            style={{ fontSize: 11, fontWeight: 600, color: T.ink, background: T.paper, border: `1px solid ${T.line}`, borderRadius: 6, padding: '2px 6px', cursor: 'pointer', maxWidth: 170 }}>
            {groupOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
      </div>
      <div className="lfe-scroll" style={{ overflow: 'auto', flex: 1 }}>
        <PanelGrid
          rows={units}
          cols={cols}
          rowKey={u => u.id}
          defaultSort={{ key: 'lot', dir: 'asc' }}
          selectedKey={selectedLotId}
          onRowClick={u => { if (u.lotId) onSelectLot(u.lotId); }}
          onRowHover={u => onHoverLot(u?.lotId ?? null)}
          rowTitle={u => `${lotLabel(u)}${u.model ? ' · ' + u.model : ''} — click to open`}
          emptyText="No units in this selection."
          groupOf={groupOpt.id === 'none' ? undefined : groupKeyOf}
          groupId={groupOpt.id}
          groupHeader={groupHeader}
          orderedRowsRef={orderedRef}
          colOrder={colOrder}
          onReorder={onReorder}
        />
      </div>
    </div>
  );
}

export default React.memo(PanelUnitsGrid);
